import type React from "react";
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useState,
} from "react";

export type ActiveInput =
  | { type: "postComment"; postId: number }
  | { type: "reply"; postId: number; parentId: number; anchorId: number }
  | null;

interface FeedInputContextType {
  activeInput: ActiveInput;
  openPostComment: (postId: number) => void;
  openReply: (postId: number, parentId: number, anchorId: number) => void;
  closeAll: () => void;
  togglePostComment: (postId: number) => void;
  toggleReply: (postId: number, parentId: number, anchorId: number) => void;
}

const FeedInputContext = createContext<FeedInputContextType | undefined>(
  undefined,
);

export const FeedInputProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [activeInput, setActiveInput] = useState<ActiveInput>(null);
  const toValidId = useCallback(
    (value: unknown, label: string): number | null => {
      const num = Number(value);
      if (!Number.isFinite(num) || num <= 0) {
        console.error(`FeedInputContext received invalid ${label}:`, value);
        return null;
      }
      return num;
    },
    [],
  );

  const openPostComment = useCallback(
    (postId: number) => {
      const normalizedPostId = toValidId(postId, "postId");
      if (normalizedPostId === null) return;
      setActiveInput({ type: "postComment", postId: normalizedPostId });
    },
    [toValidId],
  );

  const openReply = useCallback(
    (postId: number, parentId: number, anchorId: number) => {
      const normalizedPostId = toValidId(postId, "postId");
      const normalizedParentId = toValidId(parentId, "parentId");
      const normalizedAnchorId = toValidId(anchorId, "anchorId");
      if (!normalizedPostId || !normalizedParentId || !normalizedAnchorId)
        return;
      setActiveInput({
        type: "reply",
        postId: normalizedPostId,
        parentId: normalizedParentId,
        anchorId: normalizedAnchorId,
      });
    },
    [toValidId],
  );

  const closeAll = useCallback(() => {
    setActiveInput(null);
  }, []);

  const togglePostComment = useCallback(
    (postId: number) => {
      setActiveInput((prev) => {
        const normalizedPostId = toValidId(postId, "postId");
        if (normalizedPostId === null) return prev;
        if (prev?.type === "postComment" && prev.postId === normalizedPostId) {
          return null;
        }
        return { type: "postComment", postId: normalizedPostId };
      });
    },
    [toValidId],
  );

  const toggleReply = useCallback(
    (postId: number, parentId: number, anchorId: number) => {
      setActiveInput((prev) => {
        const normalizedPostId = toValidId(postId, "postId");
        const normalizedParentId = toValidId(parentId, "parentId");
        const normalizedAnchorId = toValidId(anchorId, "anchorId");
        if (!normalizedPostId || !normalizedParentId || !normalizedAnchorId)
          return prev;
        if (
          prev?.type === "reply" &&
          prev.postId === normalizedPostId &&
          prev.parentId === normalizedParentId &&
          prev.anchorId === normalizedAnchorId
        ) {
          return null;
        }
        return {
          type: "reply",
          postId: normalizedPostId,
          parentId: normalizedParentId,
          anchorId: normalizedAnchorId,
        };
      });
    },
    [toValidId],
  );

  const value = {
    activeInput,
    openPostComment,
    openReply,
    closeAll,
    togglePostComment,
    toggleReply,
  };

  return (
    <FeedInputContext.Provider value={value}>
      {children}
    </FeedInputContext.Provider>
  );
};

export const useFeedInput = () => {
  const context = useContext(FeedInputContext);
  if (context === undefined) {
    throw new Error("useFeedInput must be used within a FeedInputProvider");
  }
  return context;
};
